import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import StyledButton from "components/shared/StyledButton";
import type { IBeneficiar, IRepresentative } from "containers/Beneficiars/types";
import type { DeclarationsCardsData, URSSAFBeneficiaryError } from "containers/DeclarationPage/utils";
import { getDeclarationPeriod, savePaymentFetchType } from "containers/DeclarationPage/utils";
import { getArrayFromObject, getKeyFromPair, isObjectEmpty } from "helpers/utils";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import type { FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { updateBeneficiary, updateRepresentative } from "store/beneficiaryStore";
import type { IDeclaration } from "store/contributionStore";
import { fetchPaymentsBetweenDates, fetchURSSAFErrors } from "store/paymentSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";

import type { FormContainerRefs } from "./IndividualForm";
import IndividualForm from "./IndividualForm";
import styles from "./style.module.scss";

type BeneficiaryFormValues = {
    socialSecurityNumber: string | undefined;
    siretNumber: string | undefined;
    street: string | undefined;
    zipCode: string | undefined;
    city: string | undefined;
    country: { name: string; flag: string };
};

export const getErrorI18nTable = () => {
    return {
        UnauthorizedProfileType: t("URSSAFErrors.UnauthorizedProfileType"),
        MissingMandatoryField: t("URSSAFErrors.MissingMandatoryField"),
        WrongDateFormat: t("URSSAFErrors.WrongDateFormat"),
        WrongLength: t("URSSAFErrors.WrongLength"),
        WrongControlKey: t("URSSAFErrors.WrongControlKey"),
        RequiredField: t("URSSAFErrors.RequiredField"),
        WrongValue: t("URSSAFErrors.WrongValue"),
    };
};

export interface FormRefs {
    [id: number]: FormContainerRefs;
}

export type TSubmitData = IRepresentative | IBeneficiar;

interface IBeneficiaryForm {
    beneficiaryId: number | undefined;
    /**
     * URSSAF errors linked to this beneficiary.
     */
    beneficiaryErrors: URSSAFBeneficiaryError | undefined;
    /**
     * Data of the declaration being done.
     */
    declarationData: IDeclaration | DeclarationsCardsData | undefined;
    /**
     * Closes modal.
     */
    handleClose: () => void;
}

export type TFormattedData = (
    | (IBeneficiar & { type: "beneficiary" })
    | (IRepresentative & { type: "representatives" })
) &
    URSSAFBeneficiaryError;

/**
 * Within Declaration modal, modal to update beneficiary/representative data when there are URSSAF errors.
 */
const UpdatesModal = ({ beneficiaryId, beneficiaryErrors, declarationData, handleClose }: IBeneficiaryForm) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const [submitPending, setSubmitPending] = useState(false);
    const [formErrors, setFormErrors] = useState<Record<number, FieldErrors<BeneficiaryFormValues>>>({});
    const [formRefs, setFormRefs] = useState<FormRefs>({});
    const [hasLoaded, setHasLoaded] = useState(false);

    // Data including all field values for submit:
    const [data, setData] = useState<TFormattedData>({} as TFormattedData);

    const { beneficiar: beneficiary } = useAppSelector((state) => state.beneficiary);

    /**
     * Array with data for each individual being updated + their errors.
     * Data is within IPayment. So instead of passing all payment data, we retrieve only data needed.
     */
    const formattedPersonData: TFormattedData = useMemo(() => {
        if (!beneficiaryErrors) return;
        let data: any = {};
        const beneficiary = beneficiaryErrors.beneficiary;
        if (beneficiary.computeFromRepresentative) {
            data = { ...beneficiary._representative, type: "representatives", errors: beneficiaryErrors.errors };
        } else {
            data = { ...beneficiary.beneficiary, type: "beneficiary", errors: beneficiaryErrors.errors };
        }

        return data;
    }, [beneficiaryErrors]);

    const representativeName = useMemo(() => {
        if (formattedPersonData.type !== "representatives") return "";
        return `${(formattedPersonData.surname ?? "").toUpperCase()} ${formattedPersonData?.name ?? ""}`;
    }, [formattedPersonData]);

    /**
     * Handles scrolling to first error when submitting.
     */
    const handleScrollToError = () => {
        if (Object.keys(formErrors).length > 0) {
            // Get id of the first form that contains an error:
            const firstKeyWithError = Object.keys(formErrors)[0];

            // Retrieves first error of that form:
            const firstError = getArrayFromObject(formErrors[firstKeyWithError])[0];

            if (formRefs && firstError) {
                // Get all refs of fields within that form:
                const keyRefs = formRefs[firstKeyWithError];
                // Get the field that contains that first error:
                const fieldName = getKeyFromPair(firstError);
                // Get the ref of that specific field:
                const fieldRef: any = keyRefs[fieldName];
                // Scroll to it:
                fieldRef && fieldRef.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    };

    const handleSubmit = async () => {
        if (!data) return;
        setSubmitPending(true);

        // If any errors exist, don't continue, but scroll to first error:
        if (Object.keys(formErrors).length > 0) {
            setSubmitPending(false);
            handleScrollToError();
            return;
        }

        let response: TFetchStatus = "idle";
        // If a beneficiary is being updated, make dispatch:
        if (!isObjectEmpty(beneficiary)) {
            const newBeneficiaryData = data;
            const newData = {
                ...beneficiary,
                name: newBeneficiaryData.name,
                surname: newBeneficiaryData.surname,
                socialSecurityNumber: newBeneficiaryData.socialSecurityNumber,
                siretNumber: newBeneficiaryData.siretNumber,
                street: newBeneficiaryData.street,
                zipCode: newBeneficiaryData.zipCode,
                city: newBeneficiaryData.city,
                country: newBeneficiaryData.country,
                birthDate: new Date(beneficiary.birthDate),
                customFields: undefined,
            };

            response = (await dispatch(updateBeneficiary({ data: newData, id: beneficiary.id }))).meta.requestStatus;
        }

        // If representatives are being updated, make dispatch within Promise.all:
        if (data.type === "representatives" && beneficiaryId) {
            response = (
                await dispatch(updateRepresentative({ beneficiaryId: beneficiaryId, representativeId: data.id, data }))
            ).meta.requestStatus;
        }

        // Once all dispatches successful, update payments and errors info, then close modal:
        if (declarationData && response === "fulfilled") {
            const dateBoundaries = getDeclarationPeriod(declarationData.year, declarationData.quarter);

            if (dateBoundaries) {
                dispatch(
                    fetchPaymentsBetweenDates({
                        startDate: dateBoundaries.startDate,
                        endDate: dateBoundaries.endDate,
                        forURSSAF: "true",
                    }),
                );
                savePaymentFetchType("declarations");
                dispatch(
                    fetchURSSAFErrors({
                        startDate: dateBoundaries.startDate,
                        endDate: dateBoundaries.endDate,
                        downloadMode: false,
                    }),
                );
            }
            setSubmitPending(false);
            handleClose && handleClose();
        } else {
            setSubmitPending(false);
        }
    };

    // Sets initial data for submit on first render.
    // Data is then updated any time they make a change in one of the forms.
    useEffect(() => {
        if (beneficiaryErrors && isObjectEmpty(data)) {
            setData(formattedPersonData);
        }
    }, [formattedPersonData, beneficiary]);

    const showRepresentativesMessage = useMemo(() => {
        return formattedPersonData.type === "representatives";
    }, [formattedPersonData]);

    // Once data loaded and form errors too, mark component as loaded. (Needed to display chip)
    useEffect(() => {
        const isDataLoaded = !isObjectEmpty(data) && !isObjectEmpty(formattedPersonData);
        if (isDataLoaded && !hasLoaded) {
            setHasLoaded(true);
        }
    }, [data, formErrors]);

    return (
        <>
            {showRepresentativesMessage ? (
                <span className={styles["representatives-message"]}>
                    {t("Declarations.BeneficiariesTable.RepresentativesMessage", {
                        representative: representativeName,
                    })}
                </span>
            ) : (
                <></>
            )}
            <IndividualForm
                key={formattedPersonData.id}
                type={formattedPersonData.type}
                data={formattedPersonData}
                beneficiaryId={beneficiaryId}
                submitData={data as any}
                setSubmitData={setData as any}
                setFormErrors={setFormErrors}
                formErrors={formErrors}
                setFormRefs={setFormRefs}
            />

            <div className={styles["action-btns"]}>
                <StyledButton buttonType="secondary" onClick={handleClose}>
                    {t("Declarations.BeneficiariesTable.Cancel")}
                </StyledButton>
                <StyledButton buttonType="primary" loading={submitPending} onClick={handleSubmit}>
                    {t("Declarations.BeneficiariesTable.Save")}
                </StyledButton>
            </div>
        </>
    );
};

export default UpdatesModal;
